/* eslint-disable max-lines */

import { type WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import type { ApiResponse } from "@src/appV2/api";
import { useGetClipboardScoreProfile } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { type ScoreFreezeReason } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import type { FacilityRule } from "@src/appV2/Facilities/types";
import { useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ClockInChartingReminder } from "@src/appV2/redesign/Charting/ClockInChartingReminder";
import { YourChartingLoginBottomSheetContainer } from "@src/appV2/redesign/Charting/YourChartingLoginBottomSheetContainer";
import { BreakConfirmationBottomSheet } from "@src/appV2/redesign/Shift/BreakConfirmationBottomSheet/BreakConfirmationBottomSheet";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import {
  MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH,
  SHIFT_DETAILS_TIMEKEEPING_FULL_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import { isBackgroundCheckError } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/utils/handleBackgroundCheckError";
import { NoteAcknowledgementAction } from "@src/appV2/Shifts/MandatoryBreakPolicy/types";
import { type NfcTagRequest } from "@src/appV2/Shifts/Shift/types";
import type { ChartingCredential } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useEffect, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { useSkippableWait } from "../../../utils/useSkippableWait";
import { ShiftMarkedNonIpReasons, useMarkShiftNonInstant } from "../../api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  type RecordTimekeepingActionResponse,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "../../api/useRecordShiftTimekeepingAction";
import { NfcHashValidationAction } from "../NfcValidation/api/types";
import { useDisableShiftNfcCheck } from "../NfcValidation/api/useDisableShiftNfcCheck";
import { WorkerNfcValidation } from "../NfcValidation/WorkerNfcValidation";
import { DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS } from "./constants";
import { DisableShiftNfcFailed } from "./DisableShiftNfcFailed";
import { TimekeepingActionSuccessContent } from "./TimekeepingActionSuccessContent";
import { getClockInSuccessDescription } from "./utils/getClockInSuccessDescription";
import { getHasWorkerClockedInOnTime } from "./utils/getHasWorkerClockedInOnTime";
import { WorkplaceRulesReminderContent } from "./WorkplaceRulesReminderContent";

export interface ClockInBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  shiftStartDate: Date;
  earliestGeofenceEntryTime?: Date;
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  isNfcClockInEnabled: boolean;
  nfcTagRequests: NfcTagRequest[];
  breakPolicyNoteId: string;
  requiresBreakPolicyAcknowledgement: boolean;
  workplaceRules?: FacilityRule[];
  shouldShowWorkplaceRulesReminder: boolean;
  isSecondShiftInDouble: boolean;
  chartingReminderEnabled: boolean;
  chartingCredentialSharingIsEnabled: boolean;
  chartingCredential?: ChartingCredential;
  targetArrivalTime?: Date;
  timezone?: string;
  shiftBookedAt?: Date;
  isTimesheetEditDisabled: boolean;
  shouldEnforceNfcTimesheetBounds: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  workerStage: WorkerStage;
  workerSubstage?: string;
  isClipboardScoreFreezeEnabled: boolean;
  clipboardScoreFreezeReason?: ScoreFreezeReason;
  isMissedPunchRequestEnabled: boolean;
}

type ClockInBottomSheetStep =
  | "nfc-validation"
  | "requesting-clock-in"
  | "requesting-non-instant-pay"
  | "disable-shift-nfc-check-failed"
  | "clock-in-success"
  | "workplace-rules"
  | "charting-reminder";

export function ClockInBottomSheet(props: ClockInBottomSheetProps) {
  const {
    modalState,
    shiftId,
    shiftStartDate,
    earliestGeofenceEntryTime,
    workerId,
    workplaceId,
    workplaceName,
    isNfcClockInEnabled: isNfcClockInEnabledProperty,
    nfcTagRequests,
    breakPolicyNoteId,
    requiresBreakPolicyAcknowledgement: requiresBreakPolicyAcknowledgementProperty,
    workplaceRules,
    shouldShowWorkplaceRulesReminder,
    isSecondShiftInDouble,
    chartingReminderEnabled,
    chartingCredentialSharingIsEnabled,
    chartingCredential,
    targetArrivalTime,
    timezone,
    shiftBookedAt,
    isTimesheetEditDisabled,
    shouldEnforceNfcTimesheetBounds,
    isArrivalTimeFeatureEnabled,
    workerStage,
    workerSubstage,
    isClipboardScoreFreezeEnabled,
    clipboardScoreFreezeReason,
    isMissedPunchRequestEnabled,
  } = props;

  const history = useHistory();
  const chartingReminderModalState = useModalState();
  const chartingCredentialsModalState = useModalState();

  const { data: clipboardScoreProfile } = useGetClipboardScoreProfile(workerId);

  const [isNfcClockInEnabled, setIsNfcClockInEnabled] = useState(isNfcClockInEnabledProperty);

  const initialStep = isNfcClockInEnabled ? "nfc-validation" : "requesting-clock-in";

  const [step, setStep] = useState<ClockInBottomSheetStep>(initialStep);

  const { mutateAsync: recordTimekeepingAction, data: postClockInShift } =
    useRecordShiftTimekeepingAction();
  // Manual refs to track mutation state
  const isClockInPendingRef = useRef(false);
  const hasClockInSucceededRef = useRef(false);

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();
  const { mutateAsync: callDisableShiftNfcCheck } = useDisableShiftNfcCheck();

  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const { wait, skip } = useSkippableWait();

  const [requiresBreakPolicyAcknowledgement, setRequiresBreakPolicyAcknowledgement] = useState(
    requiresBreakPolicyAcknowledgementProperty
  );

  const clockInTime = postClockInShift?.data.clockInOut?.start
    ? parseISO(postClockInShift.data.clockInOut.start)
    : undefined;

  const hasWorkerClockedInOnTime = getHasWorkerClockedInOnTime({
    shiftStartDate,
    clockInTime,
  });

  const abortFlowAndClose = useCallback(() => {
    skip();
    modalState.closeModal();
    setStep(initialStep);
  }, [modalState, skip, initialStep]);

  const completeSuccessFlow = useCallback(
    async (postMutationShift?: ApiResponse<RecordTimekeepingActionResponse>) => {
      setStep("clock-in-success");
      await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);

      // Manually set shift as clocked in before the API GET call is made
      if (postMutationShift) {
        queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
          result
            ? {
                ...result,
                response: {
                  ...result.response,
                  clockInOut: postMutationShift.data.clockInOut,
                },
              }
            : undefined
        );
      }

      history.replace(
        generatePath(SHIFT_DETAILS_TIMEKEEPING_FULL_PATH, {
          shiftId,
        })
      );
    },
    [wait, queryClient, shiftId, history]
  );

  const onClockInSuccess = useCallback(
    async (postMutationShift: ApiResponse<RecordTimekeepingActionResponse>) => {
      if (shouldShowWorkplaceRulesReminder && workplaceRules && workplaceRules.length > 0) {
        setStep("workplace-rules");
      } else if (chartingReminderEnabled) {
        setStep("charting-reminder");
        chartingReminderModalState.openModal();
      } else {
        await completeSuccessFlow(postMutationShift);
      }
    },
    [
      completeSuccessFlow,
      shouldShowWorkplaceRulesReminder,
      workplaceRules,
      chartingReminderEnabled,
      chartingReminderModalState,
    ]
  );

  const recordClockIn = useCallback(async () => {
    // Check manual refs to prevent double execution. Without these, double clock-in is possible
    // if user calls disableShiftNfcCheck(), which changes state variables and triggers the below
    // useEffect. Checking .isLoading and.isSuccess on the mutation don't work, likely because
    // that state gets reset due to re-render of this component.
    if (isClockInPendingRef.current || hasClockInSucceededRef.current) {
      return;
    }

    // Mark as pending
    isClockInPendingRef.current = true;

    try {
      const postMutationShift = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_IN,
        shiftActionCheck: isNfcClockInEnabled ? ActionCheckType.NFC : ActionCheckType.LOCATION,
      });

      // Mark as succeeded
      hasClockInSucceededRef.current = true;
      await onClockInSuccess(postMutationShift);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock in, please try again.";

      logEvent(APP_V2_USER_EVENTS.CLOCK_IN_FAILED, {
        shiftId,
        workerId,
        workplaceId,
        clockInType: isNfcClockInEnabled ? "nfc" : "geofence",
        error: errorMessage,
      });

      if (isBackgroundCheckError(error)) {
        modalState.closeModal();
        history.replace(MY_SHIFTS_BACKGROUND_CHECK_ERROR_FULL_PATH);
        return;
      }

      showErrorToast(errorMessage);

      abortFlowAndClose();
    } finally {
      // Clear pending state
      isClockInPendingRef.current = false;
    }
  }, [
    recordTimekeepingAction,
    shiftId,
    isNfcClockInEnabled,
    onClockInSuccess,
    showErrorToast,
    abortFlowAndClose,
    workerId,
    workplaceId,
    history,
    modalState,
  ]);

  // Recording clock-in is usually a result of a user action somewhere in this sheet,
  // but in this specific case (the sheet has just opened and NFC is not enabled). In the future
  // it would be better to trigger this code as a result of explicit user interaction (e.g. button click)
  useEffect(() => {
    if (modalState.modalIsOpen && step === "requesting-clock-in" && !isNfcClockInEnabled) {
      void recordClockIn();
    }
  }, [modalState.modalIsOpen, step, isNfcClockInEnabled, recordClockIn]);

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: isNfcClockInEnabled
          ? ShiftMarkedNonIpReasons.NFC_FAILURE
          : ShiftMarkedNonIpReasons.NO_LOCATION,
      });

      modalState.closeModal();
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      abortFlowAndClose();
    }
  }

  async function disableShiftNfcCheck() {
    setStep("requesting-clock-in");

    try {
      await callDisableShiftNfcCheck({ shiftId, workerId });
      setIsNfcClockInEnabled(false);
    } catch {
      setStep("disable-shift-nfc-check-failed");
      return;
    }

    await recordClockIn();
  }

  return (
    <>
      {requiresBreakPolicyAcknowledgement && (
        <BreakConfirmationBottomSheet
          action={NoteAcknowledgementAction.CLOCK_IN}
          isLoading={false}
          modalState={modalState}
          shiftId={shiftId}
          workplaceId={workplaceId}
          breakPolicyNoteId={breakPolicyNoteId}
          onAcknowledge={async () => {
            setRequiresBreakPolicyAcknowledgement(false);
          }}
        />
      )}

      {!requiresBreakPolicyAcknowledgement && (
        <BottomSheet modalState={modalState} canDismiss={false}>
          {step === "nfc-validation" && (
            <WorkerNfcValidation
              shiftId={shiftId}
              metaProps={{ workerId, workplaceId, workplaceName }}
              nfcTagRequests={nfcTagRequests}
              clockAction={NfcHashValidationAction.CLOCK_IN}
              isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
              onSuccess={async () => {
                setStep("requesting-clock-in");
                await recordClockIn();
              }}
              onSkipLocationCheck={async () => {
                setStep("requesting-non-instant-pay");
                await convertToNonInstantPay();
              }}
              onCancel={() => {
                abortFlowAndClose();
              }}
              onNfcNotSupported={async () => {
                await disableShiftNfcCheck();
              }}
            />
          )}
          {step === "requesting-clock-in" && (
            <BottomSheetLoadingContent
              title="Confirming your clock in"
              description="Should only take a few seconds"
            />
          )}
          {step === "clock-in-success" && (
            <TimekeepingActionSuccessContent
              title="Have a great shift!"
              description={getClockInSuccessDescription({
                hasWorkerClockedInOnTime,
                isSecondShiftInDouble,
                earliestGeofenceEntryTime,
                targetArrivalTime,
                shiftStartDate,
                clockInTime,
                timezone,
                shiftBookedAt,
                isTimesheetEditDisabled,
                shouldEnforceNfcTimesheetBounds,
                isArrivalTimeFeatureEnabled,
                policy: clipboardScoreProfile?.policy,
                workerStage,
                workerSubstage,
                isClipboardScoreFreezeEnabled,
                clipboardScoreFreezeReason,
              })}
              onClose={() => {
                skip();
                modalState.closeModal();
                // Note that we don't call abortFlowAndClose here because it resets the step to geofence-check
                // which may call the geofence check logic again before redirecting to the post-clock-in flow.
              }}
            />
          )}
          {step === "workplace-rules" && workplaceRules && (
            <WorkplaceRulesReminderContent
              workplaceRules={workplaceRules}
              shiftId={shiftId}
              workplaceId={workplaceId}
              onContinue={async () => {
                if (chartingReminderEnabled) {
                  setStep("charting-reminder");
                  chartingReminderModalState.openModal();
                } else {
                  await completeSuccessFlow(postClockInShift);
                }
              }}
            />
          )}
          {step === "charting-reminder" && (
            <ClockInChartingReminder
              chartingReminderModalState={chartingReminderModalState}
              chartingCredentialsModalState={chartingCredentialsModalState}
              shiftId={shiftId}
              workplaceId={workplaceId}
              chartingCredential={chartingCredential}
              chartingCredentialSharingIsEnabled={chartingCredentialSharingIsEnabled}
              onClose={async () => {
                await completeSuccessFlow(postClockInShift);
              }}
            />
          )}
          {step === "requesting-non-instant-pay" && (
            <BottomSheetLoadingContent
              title="Converting to non-instant pay"
              description="Should only take a few seconds"
            />
          )}
          {step === "disable-shift-nfc-check-failed" && (
            <DisableShiftNfcFailed
              onTryAgain={async () => {
                await disableShiftNfcCheck();
              }}
              onClose={() => {
                abortFlowAndClose();
              }}
            />
          )}
        </BottomSheet>
      )}

      <YourChartingLoginBottomSheetContainer
        modalState={chartingCredentialsModalState}
        chartingCredentials={chartingCredential}
        shiftId={shiftId}
        workplaceId={workplaceId}
        workerId={workerId}
        onClose={async () => {
          await completeSuccessFlow(postClockInShift);
        }}
      />
    </>
  );
}
/* eslint-enable max-lines */
